import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldAlert, Link2, Printer, Check, ImageDown } from "lucide-react";
import { toPng } from "html-to-image";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public one-pager reader, made to feel like a proud, giftable snapshot.
 *
 * Anyone with the slug can read this page. An unknown OR revoked slug
 * returns the same plain notice (indistinguishable). This surface never
 * renders a scored result, a rank, or any derailer / prediction / tracker
 * field. It composes the same gate-verified one-pager the owner sees, minus
 * the owner-only `dropped` list.
 */
export default function PublicOnePagerScreen() {
  const { slug } = useParams();
  const [payload, setPayload] = useState(null);
  const [state, setState] = useState("loading");
  const [copied, setCopied] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/resume/shares/public/${slug}`)
      .then((r) => { setPayload(r.data); setState("ok"); })
      .catch(() => { setState("gone"); });
  }, [slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* ignore */ }
  };

  // One tap: render the snapshot card to a crisp PNG the owner can post
  // anywhere. The card is text only, so the capture is clean.
  const downloadImage = async () => {
    if (!cardRef.current) return;
    setImgBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        cacheBust: true,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "klareet-one-pager.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (_e) { /* ignore */ } finally {
      setImgBusy(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="max-w-3xl mx-auto px-5 pt-24 text-center">
        <p className="k-quiet">Opening this one-pager.</p>
      </div>
    );
  }

  if (state === "gone" || !payload) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-24 text-center">
        <ShieldAlert size={40} className="mx-auto mb-3" style={{ color: "hsl(var(--klareet-muted))" }} />
        <h1 className="klareet-serif mb-2" style={{ fontSize: "1.6rem" }} data-testid="share-gone-title">
          This link is no longer available.
        </h1>
        <p className="k-quiet" data-testid="share-gone-body">
          The owner either revoked it, or the link never existed. If you were expecting a page here, please ask them for a fresh link.
        </p>
      </div>
    );
  }

  const experiences = payload.experiences || [];
  const educations = payload.educations || [];
  const skills = payload.skills || [];
  const headline = payload.headline?.text || "";
  const summary = payload.summary?.text || "";
  const isEmpty = !headline && !summary && experiences.length === 0 && educations.length === 0 && skills.length === 0;

  let sharedOn = "";
  const created = payload.share_meta?.created_at;
  if (created) {
    const d = new Date(created);
    if (!isNaN(d)) sharedOn = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }

  return (
    <div className="k-shell relative overflow-hidden">
      <div className="k-orb k-orb-teal" style={{ top: "-9rem", right: "-7rem", width: "22rem", height: "22rem", opacity: 0.5 }} />
      <div className="k-orb k-orb-blue" style={{ bottom: "2rem", left: "-9rem", width: "22rem", height: "22rem", opacity: 0.4 }} />

      <div className="relative max-w-2xl mx-auto px-5 pt-10 pb-20">
        {/* Header row: eyebrow + owner actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="k-label" data-testid="share-eyebrow">a klareet one-pager</div>
          <div className="flex items-center gap-2 print:hidden" data-testid="share-actions">
            <button onClick={copyLink} className="k-btn-outline !py-1.5 !px-3 text-xs" data-testid="share-copy-link">
              {copied ? <><Check size={12} /> copied</> : <><Link2 size={12} /> copy link</>}
            </button>
            <button onClick={downloadImage} disabled={imgBusy} className="k-btn-outline !py-1.5 !px-3 text-xs" data-testid="share-download-image">
              <ImageDown size={12} /> {imgBusy ? "saving" : "download image"}
            </button>
            <button onClick={() => window.print()} className="k-btn-outline !py-1.5 !px-3 text-xs" data-testid="share-print">
              <Printer size={12} /> save as pdf
            </button>
          </div>
        </div>

        {/* The snapshot card */}
        <article className="k-onepager-card" data-testid="share-onepager" ref={cardRef}>
          <div className="k-onepager-accent" aria-hidden="true" />
          <div className="k-onepager-body">
            {isEmpty && (
              <div className="k-quiet !text-sm" data-testid="share-empty">
                The owner has not filled in the one-pager yet. Once they do, this page updates without a new link.
              </div>
            )}

            {headline && (
              <h1 className="klareet-serif" style={{ fontSize: "clamp(1.9rem, 5vw, 2.6rem)", lineHeight: 1.12, letterSpacing: "-0.01em" }} data-testid="share-headline">
                {headline}
              </h1>
            )}
            {summary && (
              <p className="klareet-serif mt-4" style={{ fontSize: "1.1rem", lineHeight: 1.7, color: "hsl(var(--klareet-ink-soft))" }} data-testid="share-summary">
                {summary}
              </p>
            )}

            {experiences.length > 0 && (
              <section className="mt-9" data-testid="share-experiences">
                <div className="k-onepager-rule"><span>experience</span></div>
                <div className="space-y-6 mt-4">
                  {experiences.map((e) => (
                    <div key={e.id}>
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>
                          {e.title}
                        </div>
                        {(() => {
                          const yr = (d) => (d ? new Date(d).getFullYear() : "");
                          const start = yr(e.started_on);
                          const end = e.is_current ? "now" : yr(e.ended_on);
                          const range = start && end ? `${start} to ${end}` : (end || start || "");
                          return range ? (
                            <div className="k-quiet !text-xs klareet-mono shrink-0">{range}</div>
                          ) : null;
                        })()}
                      </div>
                      <div className="k-quiet !text-sm mb-2">{e.organisation}</div>
                      <ul className="space-y-1.5 text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
                        {(e.bullets || []).filter(Boolean).map((b, i) => (
                          <li key={i} className="k-onepager-bullet">{b?.text}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {educations.length > 0 && (
              <section className="mt-9" data-testid="share-educations">
                <div className="k-onepager-rule"><span>education</span></div>
                <div className="space-y-4 mt-4">
                  {educations.map((e) => (
                    <div key={e.id}>
                      <div className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>{e.institution}</div>
                      <div className="k-quiet !text-sm">{e.programme}</div>
                      {(e.highlights || []).filter(Boolean).map((h, i) => (
                        <div key={i} className="k-quiet !text-xs mt-1">{h?.text}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {skills.length > 0 && (
              <section className="mt-9" data-testid="share-skills">
                <div className="k-onepager-rule"><span>skills</span></div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {skills.map((s) => (
                    <span key={s.id} className="k-chip" title={s.evidence || ""}>{s.name}</span>
                  ))}
                </div>
              </section>
            )}

            {/* Wordmark footer inside the card, so it travels with a screenshot/PDF */}
            <div className="k-onepager-foot" data-testid="share-wordmark">
              <span className="klareet-serif" style={{ fontWeight: 600 }}>Klareet</span>
              <span className="k-quiet !text-xs">{sharedOn ? `shared ${sharedOn}` : "a personal one-pager"}</span>
            </div>
          </div>
        </article>

        {/* Standing notice, kept quiet and out of the snapshot */}
        <p className="k-quiet !text-xs mt-5 text-center print:hidden" data-testid="share-notice" style={{ lineHeight: 1.6 }}>
          {payload.share_meta?.notice || "This one-pager is shared by its owner. It is a personal growth snapshot, not a formal test result."}
          {" "}The owner can revoke this link at any time.
        </p>
      </div>
    </div>
  );
}
